import { createCSVFile, downloadZip } from "../lib/exportCsv.ts";
import { createZip } from "../lib/createZipFile.ts";
import { getCurrentDateTime } from "../lib/getCurrentDateTime.ts";
import { CancelError, CancelManager } from "../lib/cancelManager.ts";
import { ExportProgressStatus } from "../modules/parts/ExportProgressModal.tsx";
import { getPaginatedData } from "../lib/api/getPaginatedData.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { ICustomer, ICustomerField, ICustomRole } from "../models/customer.ts";
import {
  transformFromCustomerToCsv,
  buildCustomerCsvHeaders,
  buildCustomRoleMap,
} from "../lib/transform/transformFromCustomerToCsv.ts";
import { config, CsvEncoding } from "../lib/config.ts";

interface ExportCustomerParams {
  token: string;
  role: string;
  excludeInternal: boolean;
  excludeSuspended: boolean;
  createdStartDate: string;
  createdEndDate: string;
  updatedStartDate: string;
  updatedEndDate: string;
  /** 組織フィルタで選択された組織（空 = 全組織スキャン） */
  selectedOrgs: { id: number; name: string }[];
  /** 全組織リスト（組織フィルタ未指定時のスキャン対象） */
  allOrgs: { id: number; name: string }[];
  nameKeyword: string;
  selectedIndustries: string[];
  addressKeyword: string;
  departmentKeyword: string;
  onUpdateTotalCount: (count: number) => void;
  onUpdateDownloadedCount: (count: number) => void;
  onUpdateFetchedCount: (count: number) => void;
  onUpdateInfo: (status: ExportProgressStatus, message: string) => void;
  csvEncoding: CsvEncoding;
}

export const exportCustomerData = async (params: ExportCustomerParams) => {
  const {
    token,
    role,
    excludeInternal,
    excludeSuspended,
    createdStartDate,
    createdEndDate,
    updatedStartDate,
    updatedEndDate,
    selectedOrgs,
    allOrgs,
    nameKeyword,
    selectedIndustries,
    addressKeyword,
    departmentKeyword,
    onUpdateTotalCount,
    onUpdateDownloadedCount,
    onUpdateFetchedCount,
    onUpdateInfo,
    csvEncoding,
  } = params;

  const cancelManager = CancelManager.getInstance();
  cancelManager.reset();

  try {
    const baseUrl = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com`;

    // 1. マスタデータ取得
    onUpdateInfo("Export", "マスタデータを取得しています。(1/3)");
    const [userFieldsRaw, customRolesRaw] = await Promise.all([
      zendeskHttpRequest<{ user_fields: ICustomerField[] }>(
        "user_fields.json",
        token,
        "GET",
      ),
      zendeskHttpRequest<{ custom_roles: ICustomRole[] }>(
        "custom_roles.json",
        token,
        "GET",
      ),
    ]);

    const activeFields = userFieldsRaw.user_fields.filter((f) => f.active);
    const customRoleMap = buildCustomRoleMap(customRolesRaw.custom_roles);

    cancelManager.checkCancelled();

    // 2. orgMap を構築（CSV変換で組織名を引くために使用）
    const orgMap = new Map(allOrgs.map((o) => [o.id, o.name]));

    // 社内除外: 機能フラグが有効な時のみ、環境変数で指定した org ID を除外
    const internalOrgIdEnv =
      config.enableInternalOrgExclusion && import.meta.env.VITE_INTERNAL_ORG_ID
        ? Number(import.meta.env.VITE_INTERNAL_ORG_ID)
        : undefined;
    const internalOrgId = excludeInternal ? internalOrgIdEnv : undefined;

    // 3. ユーザー取得
    onUpdateInfo("Export", "ユーザーを取得しています。(2/3)");
    let users: ICustomer[];

    if (selectedOrgs.length > 0) {
      // 組織指定あり: 組織ごとに取得
      const targetOrgs = internalOrgId !== undefined
        ? selectedOrgs.filter((o) => o.id !== internalOrgId)
        : selectedOrgs;

      if (targetOrgs.length === 0) {
        onUpdateInfo("Success", "エクスポート対象の組織が見つかりませんでした。");
        return;
      }

      const userMap = new Map<number, ICustomer>();
      let totalFetched = 0;

      for (const org of targetOrgs) {
        cancelManager.checkCancelled();
        const fetchedBefore = totalFetched;
        const orgUsers = await getPaginatedData<ICustomer>(
          `${baseUrl}/api/v2/organizations/${org.id}/users.json`,
          token,
          baseUrl,
          "users",
          (countInOrg) => onUpdateFetchedCount(fetchedBefore + countInOrg),
          500,
        );
        totalFetched += orgUsers.length;
        for (const user of orgUsers) {
          userMap.set(user.id, user);
        }
      }
      users = [...userMap.values()];
    } else {
      // 組織未選択: users APIから一括取得
      users = await getPaginatedData<ICustomer>(
        "users.json",
        token,
        baseUrl,
        "users",
        (count) => onUpdateFetchedCount(count),
        500,
      );

      // 社内ユーザー除外
      if (internalOrgId !== undefined) {
        users = users.filter((u) => u.organization_id !== internalOrgId);
      }
    }

    if (users.length === 0) {
      onUpdateInfo(
        "Success",
        "エクスポート対象の顧客データはありませんでした。",
      );
      return;
    }

    cancelManager.checkCancelled();

    // 4. クライアント側フィルタリング
    let filtered = users;

    if (role === "end_user") {
      filtered = filtered.filter((u) => u.role === "end-user");
    } else if (role === "agent") {
      filtered = filtered.filter(
        (u) => u.role === "agent" || u.role === "admin",
      );
    }

    if (excludeSuspended) {
      filtered = filtered.filter((u) => !u.suspended);
    }

    if (createdStartDate) {
      filtered = filtered.filter(
        (u) => u.created_at.slice(0, 10) >= createdStartDate,
      );
    }
    if (createdEndDate) {
      filtered = filtered.filter(
        (u) => u.created_at.slice(0, 10) <= createdEndDate,
      );
    }
    if (updatedStartDate) {
      filtered = filtered.filter(
        (u) => u.updated_at.slice(0, 10) >= updatedStartDate,
      );
    }
    if (updatedEndDate) {
      filtered = filtered.filter(
        (u) => u.updated_at.slice(0, 10) <= updatedEndDate,
      );
    }

    if (nameKeyword) {
      const kw = nameKeyword.toLowerCase();
      filtered = filtered.filter(
        (u) => u.name?.toLowerCase().includes(kw),
      );
    }
    if (selectedIndustries.length > 0) {
      filtered = filtered.filter((u) => {
        const v = u.user_fields?.["industry"];
        return typeof v === "string" && selectedIndustries.includes(v);
      });
    }
    if (addressKeyword) {
      const kw = addressKeyword.toLowerCase();
      filtered = filtered.filter((u) => {
        const v = u.user_fields?.["address"];
        return typeof v === "string" && v.toLowerCase().includes(kw);
      });
    }
    if (departmentKeyword) {
      const kw = departmentKeyword.toLowerCase();
      filtered = filtered.filter((u) => {
        const v = u.user_fields?.["department"];
        return typeof v === "string" && v.toLowerCase().includes(kw);
      });
    }

    if (filtered.length === 0) {
      onUpdateInfo(
        "Success",
        "フィルター条件に一致する顧客データはありませんでした。",
      );
      return;
    }

    onUpdateTotalCount(filtered.length);

    // 5. CSV変換
    onUpdateInfo("Export", "CSVファイルを作成しています。(3/3)");
    const csvData = filtered.map((user, index) => {
      onUpdateDownloadedCount(index + 1);
      return transformFromCustomerToCsv(user, orgMap, customRoleMap, activeFields);
    });

    const csvHeaders = buildCustomerCsvHeaders(activeFields);

    // ダウンロード
    onUpdateInfo("Success", "");
    const csvFile = createCSVFile(csvData, csvHeaders, csvEncoding);
    const zip = await createZip(csvFile, "export.csv", []);
    downloadZip(zip, `customers_${getCurrentDateTime()}`);
  } catch (e) {
    console.error(e);
    if (e instanceof CancelError) {
      onUpdateInfo("Cancel", "");
    } else {
      onUpdateInfo("Error", `${e}`);
    }
  }
};
